/**
 * React-query bindings for the workshop API used by the technician app:
 * the task-first Today feed, the WO detail tree, and task claiming.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { workshopApi } from '@/apis/workshopApis'
import { taskMinutes } from '@/lib/taskTime'
import type {
  DoListResponse,
  DoListServiceState,
  ManualLabourRequest,
  ProposeServiceRequest,
  ServiceResponse,
  WorkOrderDetail,
  WorkOrderResponse,
} from '@/apis/types'

const doListKey = ['workshop', 'do-list'] as const
const woDetailKeyPrefix = ['workshop', 'wo-detail'] as const
const myWorkOrdersKey = ['workshop', 'my-work-orders'] as const

/** The task-first home feed (WO → service → task). */
export function useDoList() {
  return useQuery<DoListResponse>({
    queryKey: doListKey,
    queryFn: workshopApi.getDoList,
    staleTime: 15_000,
  })
}

/**
 * Every WO assigned to me — the board's history and its future.
 *
 * Only the DONE and UPCOMING filters need it, so it is `enabled`-gated:
 * the default board must not pay for a second request it never reads.
 * Staler than the do-list on purpose — finished work does not move.
 */
export function useMyWorkOrders(enabled: boolean) {
  return useQuery<WorkOrderResponse[]>({
    queryKey: myWorkOrdersKey,
    queryFn: workshopApi.getMyWorkOrders,
    enabled,
    staleTime: 60_000,
  })
}

/** The WO detail tree (WO → service → task) for the detail page. */
export function useWorkOrderDetail(id: string | undefined) {
  return useQuery<WorkOrderDetail>({
    queryKey: [...woDetailKeyPrefix, id ?? ''],
    queryFn: () => workshopApi.getWorkOrderDetail(id!),
    enabled: Boolean(id),
  })
}

/** Task actions (start/pause/done) are reachable from BOTH the do-list
 *  and a WO's detail page, so every mutation below invalidates both query
 *  families — a claim made from the detail view must still move the row
 *  on Today, and vice versa. `wo-detail` invalidation is a prefix match
 *  (no taskId → workOrderId lookup on the client), so it refetches
 *  whichever WO detail query is currently mounted. */
function invalidateTaskViews(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: doListKey });
  qc.invalidateQueries({ queryKey: woDetailKeyPrefix });
}

/**
 * Take the whole job — the one claim. Invalidates on success AND on error:
 * a lost race (a colleague took it a second earlier, 409) has to refetch so
 * the stale card drops instead of lingering as claimable.
 */
export function useClaimWorkOrder() {
  const qc = useQueryClient()
  return useMutation<WorkOrderResponse, Error, string>({
    mutationFn: (workOrderId) => workshopApi.claimWorkOrder(workOrderId),
    onSettled: () => invalidateTaskViews(qc),
  })
}

/**
 * Take one loose service — the per-service counterpart to
 * `useClaimWorkOrder`, offered once the caller already holds a stake
 * elsewhere on the job. Same invalidation: both the do-list card and the WO
 * detail page can render the row this affects.
 */
export function useClaimService() {
  const qc = useQueryClient()
  return useMutation<ServiceResponse, Error, { workOrderId: string; serviceId: string }>({
    mutationFn: ({ workOrderId, serviceId }) => workshopApi.claimService(workOrderId, serviceId),
    onSettled: () => invalidateTaskViews(qc),
  })
}

// Job-level notes ride on both the do-list card and the job page, so every
// write refetches both families.
export function useAddWorkOrderNote() {
  const qc = useQueryClient()
  return useMutation<WorkOrderResponse, Error, { workOrderId: string; body: string }>({
    mutationFn: ({ workOrderId, body }) => workshopApi.addWorkOrderNote(workOrderId, body),
    // Returned, so the send stays pending until the thread has refetched
    // and the note is on screen — not just until the server said yes, which
    // cleared the composer a beat before the note appeared (2026-09-23).
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: doListKey }),
        qc.invalidateQueries({ queryKey: woDetailKeyPrefix }),
      ]),
  })
}



/**
 * Rewrite tasks inside a WO → service → task tree.
 *
 * `map` sees every task and returns it changed or untouched. Returning the
 * same object is the signal that nothing moved: services and work orders
 * whose tasks all come back identical are handed back by reference, so React
 * re-renders the rows that changed rather than the whole feed.
 *
 * It visits every task rather than seeking one id because starting a timer
 * moves two rows — see `useStartTask`.
 */
function patchServicesInTree<
  Service extends TimedTask,
  Tree extends { readonly services: ReadonlyArray<Service> },
>(tree: Tree, map: (service: Service) => Service): Tree {
  let treeChanged = false
  const services = tree.services.map((service) => {
    const next = map(service)
    if (next !== service) treeChanged = true
    return next
  })
  return treeChanged ? { ...tree, services } : tree
}

/** The service fields a timer action moves. Both cache shapes carry all four. */
interface TimedTask {
  readonly id: string
  readonly state: DoListServiceState
  readonly mine: boolean
  readonly runningSince: string | null
  readonly loggedMinutes: number
}

/** Stop a running task exactly as the server will: timer closed, segment
 *  banked. Used for the task being paused AND for the one a Start displaces. */
function pausedTask<T extends TimedTask>(task: T, now: number): T {
  return { ...task, state: 'paused', runningSince: null, loggedMinutes: bankedMinutes(task, now) }
}

/** Closed minutes the running segment is worth right now — what the server
 *  will bank when the pause lands. Without it the label drops to the last
 *  server figure on pause and jumps back up on the refetch.
 *
 *  Takes `now` rather than reading `Date.now()` itself — this runs inside
 *  an `onMutate` callback (not a render), so there is no `useNow()` tick to
 *  read; the caller captures one instant and threads it through instead,
 *  which is also what makes this testable as a pure function. */
function bankedMinutes(task: TimedTask, now: number): number {
  return taskMinutes(task, now)
}

type TimerSnapshot = ReturnType<typeof snapshotTaskViews>

function snapshotTaskViews(qc: ReturnType<typeof useQueryClient>) {
  return {
    doList: qc.getQueriesData<DoListResponse>({ queryKey: doListKey }),
    details: qc.getQueriesData<WorkOrderDetail>({ queryKey: woDetailKeyPrefix }),
  }
}

/**
 * Apply a timer move to both caches before the request goes out, and hand
 * back what was there so a failure can put it straight back.
 *
 * The tech taps in a workshop, often on patchy wifi. Waiting for the round
 * trip meant the row sat unchanged until the refetch landed and then flipped
 * all at once — which read as the app ignoring the tap, then lurching.
 * In-flight queries are cancelled first so a response already on its way
 * cannot overwrite the optimistic state a moment later.
 */
async function applyTimerPatch(
  qc: ReturnType<typeof useQueryClient>,
  map: <T extends TimedTask>(task: T) => T,
): Promise<TimerSnapshot> {
  await qc.cancelQueries({ queryKey: doListKey })
  await qc.cancelQueries({ queryKey: woDetailKeyPrefix })
  const previous = snapshotTaskViews(qc)

  qc.setQueriesData<DoListResponse>({ queryKey: doListKey }, (data) =>
    data
      ? { ...data, workOrders: data.workOrders.map((wo) => patchServicesInTree(wo, map)) }
      : data,
  )
  qc.setQueriesData<WorkOrderDetail>({ queryKey: woDetailKeyPrefix }, (data) =>
    data ? patchServicesInTree(data, map) : data,
  )
  return previous
}

/** Put both caches back exactly as they were — the action didn't happen. */
function rollbackTaskViews(qc: ReturnType<typeof useQueryClient>, snapshot?: TimerSnapshot) {
  if (!snapshot) return
  for (const [key, data] of snapshot.doList) qc.setQueryData(key, data)
  for (const [key, data] of snapshot.details) qc.setQueryData(key, data)
}

/**
 * Start (or resume) the caller's own timer on a task — both the do-list's
 * Start and Resume buttons call this; the backend only distinguishes "begin
 * timing now", not two separate actions.
 *
 * Optimistic, and it moves TWO rows, because the server does:
 * `ServiceExecutionService.startLabour` closes any other open labour entry
 * for the technician before opening this one — one running job per tech, the
 * "single-active-labour invariant". Patching only the started row left the
 * displaced one reading as running until the refetch corrected it, so for a
 * round trip the board showed two live timers, which is a state that cannot
 * exist. The handoff is now instant and in one direction.
 *
 * `runningSince` is stamped client-side so the timer counts from the tap; the
 * refetch replaces it with the server's instant, and a second of drift on a
 * minutes-granularity label is invisible.
 */
export function useStartTask() {
  const qc = useQueryClient()
  return useMutation<ServiceResponse, Error, string, TimerSnapshot>({
    mutationFn: (serviceId) => workshopApi.startServiceLabour(serviceId),
    onMutate: (serviceId) => {
      // One instant for the whole patch — every task in the tree that this
      // tap touches banks against the same "now", not a slightly different
      // one per row.
      const now = Date.now()
      return applyTimerPatch(qc, (service) => {
        if (service.id === serviceId) {
          return { ...service, state: 'in_progress', runningSince: new Date().toISOString() }
        }
        // The one this Start displaces. A colleague's timer is untouched —
        // the invariant is per technician, not per shop.
        if (service.mine && service.state === 'in_progress') return pausedTask(service, now)
        return service
      })
    },
    onError: (_err, _serviceId, snapshot) => rollbackTaskViews(qc, snapshot),
    onSettled: () => invalidateTaskViews(qc),
  })
}

/** Pause the caller's own running timer on a task.
 *
 *  Optimistic, and it banks the running segment into `loggedMinutes` as it
 *  goes — pausing has to leave the total where the tech last saw it. */
export function usePauseTask() {
  const qc = useQueryClient()
  return useMutation<ServiceResponse, Error, string, TimerSnapshot>({
    mutationFn: (serviceId) => workshopApi.pauseServiceLabour(serviceId),
    onMutate: (serviceId) => {
      const now = Date.now()
      return applyTimerPatch(qc, (service) => (service.id === serviceId ? pausedTask(service, now) : service))
    },
    onError: (_err, _serviceId, snapshot) => rollbackTaskViews(qc, snapshot),
    onSettled: () => invalidateTaskViews(qc),
  })
}

/** Mark my task done (OPEN → DONE). Needs the WO id for the endpoint path;
 *  both the do-list and detail views refetch so the row folds to "done". */
export function useCompleteTask() {
  const qc = useQueryClient()
  return useMutation<ServiceResponse, Error, { workOrderId: string; serviceId: string }>({
    mutationFn: ({ serviceId }) => workshopApi.completeService(serviceId),
    onSettled: () => invalidateTaskViews(qc),
  })
}

/** Finish the whole WO (assignee-only; 409 if it has no labour/parts). */
export function useCompleteWorkOrder() {
  const qc = useQueryClient()
  return useMutation<void, Error, { workOrderId: string; mileageAtComplete?: number }>({
    mutationFn: ({ workOrderId, mileageAtComplete }) =>
      workshopApi.completeWorkOrder(workOrderId, mileageAtComplete),
    onSettled: () => invalidateTaskViews(qc),
  })
}

/** Manually log time on a task (lands in the same total as the live timer). */
export function useAddTaskManualLabour() {
  const qc = useQueryClient()
  return useMutation<unknown, Error, { serviceId: string; body: ManualLabourRequest }>({
    mutationFn: ({ serviceId, body }) => workshopApi.addServiceManualLabour(serviceId, body),
    onSettled: () => invalidateTaskViews(qc),
  })
}

/** Propose additional work (creates a PENDING_APPROVAL service). */
export function useProposeService() {
  const qc = useQueryClient()
  return useMutation<ServiceResponse, Error, { workOrderId: string; body: ProposeServiceRequest }>({
    mutationFn: ({ workOrderId, body }) => workshopApi.proposeService(workOrderId, body),
    onSettled: () => invalidateTaskViews(qc),
  })
}
